import Formatter from '@sas-dvr/ltjs-commons/formatters/Formatter';
import { getFormatLocale, getLegacyLanguageTag } from '@sas-dvr/ltjs-commons/ltjsUtil';
import ResourceManager from '@sas-dvr/ltjs-commons/resources/ResourceManager';
import LocalizationContext from '@sas-dvr/ltjs-commons/resources/localization/LocalizationContext';
import FormatterFactory from '@sas-dvr/ltjs-commons/formatters/FormatterFactory';
import { isInitialized, whenInitialized } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { jsDateToSASDate, jsDateToSASDateTime, jsDateToSASTime, sasDateToJsDate, sasDateTimeToJsDate, sasTimeToJsDate, } from './CalendarUtil';
var formatterCache = {};
var contextCache = {};
/**
 * @private
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.getFormatInstance
 * @param {string} [sFormat] format name such as 'DATETIME', 'DATE24.', 'PERCENT8.2', 'BEST8.4.'.
 * @param {string} [sLocaleName] Locale name.
 * @returns {string} key name that is used for cache
 */
export function getFormatCacheKeyName(sFormat, sLocaleName) {
    return sLocaleName.replace(/_/g, '-').toUpperCase() + '_' + sFormat.toUpperCase();
}
/**
 * @private
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.getFormatInstance
 * @param {string} [sFormat] format name such as 'DATETIME', 'DATE24.', 'PERCENT8.2', 'BEST8.4.'.
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {string|null} formatter instance or null when the sFormatName is
 *                       invalid or the LTJS portable library isn't initialized yet.
 */
export function getFormatInstance(sFormat, sLocaleName) {
    if (!sLocaleName) {
        sLocaleName = getFormatLocale();
    }
    var sKey = getFormatCacheKeyName(sFormat, sLocaleName);
    var oFormat = formatterCache[sKey];
    if (oFormat) {
        return oFormat;
    }
    var oContext = contextCache[sLocaleName];
    if (!oContext) {
        // Create a localization context and set the current OpenUI format locale in the context
        var localizationContext = ResourceManager.getLocalizationContext();
        if (!localizationContext) {
            return null;
        }
        oContext = localizationContext.getMutableCopy().retain();
        oContext.setFormatterLocale(getLegacyLanguageTag(sLocaleName), 0);
        contextCache[sLocaleName] = oContext;
    }
    oFormat = FormatterFactory.instance().getFormatter(oContext, sFormat);
    if (oFormat) {
        oFormat = formatterCache[sKey] = oFormat.retain();
    }
    return oFormat;
}
/**
 * static function to format the value according to the provided format.
 * @public
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.format
 * @param {(number|string|Date)} oValue Value to be formatted
 * @param {string} [sFormat] format name such as 'DATETIME', 'DATE24.', 'PERCENT8.2', 'BEST8.4.'.
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {string|null} formatted string or null when the sFormatName is
 *                       invalid or the LTJS portable library isn't initialized yet.
 * @function
 */
export function format(oValue, sFormat, sLocaleName) {
    // tslint:disable-next-line:strict-type-predicates
    if (oValue === null || !sFormat || !isInitialized()) {
        return null;
    }
    // Use the format
    var oFormat = getFormatInstance(sFormat, sLocaleName);
    if (!oFormat) {
        return null;
    }
    if (oValue instanceof Date) {
        if (oFormat.isDateFormat()) {
            oValue = jsDateToSASDate(oValue);
        }
        else if (oFormat.isDateTimeFormat()) {
            oValue = jsDateToSASDateTime(oValue);
        }
        else if (oFormat.isTimeFormat()) {
            oValue = jsDateToSASTime(oValue);
        }
        else {
            return null;
        }
    }
    return oFormat.format(oValue);
}
/**
 * static function to parse the formatted string by the provided format.
 * @public
 * @static
 * @name sas.ltjs.sasicu.util.SASFormatterUtil.parse
 * @param {string} [sFormattedString] string that is parsed
 * @param {string} [sFormat] format name such as 'DATETIME', 'DATE24.', 'PERCENT8.2', 'BEST8.4.'.
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {number|Date|NaN} <ul>
 *                               <li>number: When the format is a numeric format.
 *                               <li>Date  : When the format is SASDATE, SASTIME or SASDATETIME, the function returns an instance of Date.
 *                               <li>NaN   : means an error happened.
 *                               <p><ul>
 *                                   <li>The sFormatName is invalid
 *                                   <li>The given sFormattedString doesn't match with the format pattern
 *                                   <li>The parse() method isn't supported by the specified formatter.
 *                                   <li>The LTJS portable library isn't initialized.
 *                               </ul></p>
 *                           </ul>
 * @function
 */
export function parse(sFormattedString, sFormat, sLocaleName) {
    if (!sFormattedString || !sFormat || !isInitialized()) {
        return NaN;
    }
    // Parse the sFormattedString
    var oFormat = getFormatInstance(sFormat, sLocaleName);
    if (oFormat) {
        var nValue = oFormat.parse(sFormattedString);
        if (oFormat.isDateFormat()) {
            return sasDateToJsDate(nValue);
        }
        else if (oFormat.isDateTimeFormat()) {
            return sasDateTimeToJsDate(nValue);
        }
        else if (oFormat.isTimeFormat()) {
            return sasTimeToJsDate(nValue);
        }
        else if (oFormat.isNumericFormat()) {
            return nValue;
        }
    }
    return NaN;
}
/**
 * static function to get a formatter instance.
 * @public
 * @static
 * @name sas.ltjs.sasicu.util.SASFormatterUtil.instance
 * @param {string} sFormat Output required format
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {sas.ltjs.commons.formatters.Formatter} formatter instance
 * @function
 */
export function instance(sFormat, sLocaleName) {
    return getFormatInstance(sFormat, sLocaleName);
}
/**
 * static function to get a string without BIDI RLE, LRE, PDF characters from the string.
 * @public
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.unfinalizeString
 * @param {string} sValue a formatted string that can have BIDI RLE, LRE and PDF.
 * @returns {string|null} string that doesn't have RLE, LRE and PDF. null when the LTJS
 *                  portable library isn't initialized yet
 * @function
 */
export function unfinalizeString(sValue) {
    if (!sValue || !isInitialized()) {
        return null;
    }
    return Formatter.unfinalizeString(sValue);
}
/**
 * static function to format the value according to the provided format.
 * @public
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.formatPromise
 * @param {(number|string|Date)} oValue Value to be formatted
 * @param {string} sFormat Output required format
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {Promise} Its then() function gets the function whose parameter is a formatted string
 * @function
 */
export function formatPromise(oValue, sFormat, sLocaleName) {
    return whenInitialized().then(function () {
        return format(oValue, sFormat, sLocaleName);
    });
}
/**
 * static function to format the value according to the provided format.
 * @public
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.formatWithoutBidiPromise
 * @param {(number|string|Date)} oValue Value to be formatted
 * @param {string} sFormat Output required format
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {Promise} Its then() function gets the function whose parameter is a formatted string
 * @function
 */
export function formatWithoutBidiPromise(oValue, sFormat, sLocaleName) {
    return whenInitialized().then(function () {
        // tslint:disable-next-line:strict-type-predicates
        if (oValue === null || !sFormat || !isInitialized()) {
            return null;
        }
        // Use the format
        var oFormat = getFormatInstance(sFormat, sLocaleName);
        if (!oFormat) {
            return null;
        }
        if (oValue instanceof Date) {
            if (oFormat.isDateFormat()) {
                oValue = jsDateToSASDate(oValue);
            }
            else if (oFormat.isDateTimeFormat()) {
                oValue = jsDateToSASDateTime(oValue);
            }
            else if (oFormat.isTimeFormat()) {
                oValue = jsDateToSASTime(oValue);
            }
            else {
                return null;
            }
        }
        return oFormat.formatWithoutBidi(oValue);
    });
}
/**
 * static function to parse the formatted string by the provided format.
 * @public
 * @static
 * @name sas.ltjs.sasicu.util.SASFormatterUtil.parsePromise
 * @param {string} sFormattedString string that is parsed
 * @param {string} sFormat format name such as 'DATETIME', 'DATE24.', 'PERCENT8.2', 'BEST8.4.'.
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {Promise} Its then() function gets the function whose parameter is one of the following values
 * <ul>
 *     <li>number: When the format is a numeric format.
 *     <li>Date  : When the format is SASDATE, SASTIME or SASDATETIME, the function returns an instance of Date.
 *     <li>NaN   : means an error happened.
 *         <p><ul>
 *             <li>The sFormatName is invalid
 *             <li>The given sFormattedString doesn't match with the format pattern
 *             <li>The parse() method isn't supported by the specified formatter.
 *             <li>The LTJS portable library isn't initialized.
 *         </ul></p>
 * </ul>
 * @function
 */
export function parsePromise(sFormattedString, sFormat, sLocaleName) {
    return whenInitialized().then(function () {
        return parse(sFormattedString, sFormat, sLocaleName);
    });
}
/**
 * static function to get a formatter instancePromise.
 * @public
 * @static
 * @name sas.ltjs.sasicu.util.SASFormatterUtil.instance
 * @param {string} sFormat Output required format
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {Promise} Its then() function gets the function whose parameter is a formatter instance or null when the creation of the formatter fails
 * @function
 */
export function instancePromise(sFormat, sLocaleName) {
    return whenInitialized().then(function () {
        return instance(sFormat, sLocaleName);
    });
}
/**
 * static function to get a string without BIDI RLE, LRE, PDF characters from the string.
 * @public
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.unfinalizeStringPromise
 * @param {string} sValue a formatted string that can have BIDI RLE, LRE and PDF.
 * @returns {Promise} Its then() function gets the function whose parameter is a string
 *                   that doesn't have RLE, LRE and PDF.
 * @function
 */
export function unfinalizeStringPromise(sValue) {
    return whenInitialized().then(function () {
        return unfinalizeString(sValue);
    });
}
/**
 * static function to get a string with BIDI RLE, LRE, PDF characters.
 * @public
 * @static
 * @name sas.ltjf.sasicu.util.SASFormatterUtil.finalizeStringPromise
 * @param {string} sValue a string
 * @param {string} [sLocaleName] Locale name. If it's omitted, the locale name
 *                  sap.ui.getCore().getConfiguration().getFormatLocale() is used.
 * @returns {Promise} Its then() function gets the function whose parameter is a string
 *                   that is wrapped by RLE, LRE and PDF.
 * @function
 */
export function finalizeStringPromise(sValue, sLocaleName) {
    return whenInitialized().then(function () {
        var oFormat = getFormatInstance('comma', sLocaleName);
        if (!oFormat) {
            return null;
        }
        return oFormat.finalizeString(sValue);
    });
}
/**
 * static function to explicitly clear the caches of Formatter and LocalizationContext
 * This function isn't supposed to be called unless memory runs out.
 * @private
 * @static
 * @function
 */
export function clearCache() {
    for (var prop in formatterCache) {
        if (formatterCache.hasOwnProperty(prop)) {
            if (formatterCache[prop] instanceof Formatter && formatterCache[prop].isRetained()) {
                formatterCache[prop].release();
                // tslint:disable-next-line:no-dynamic-delete
                delete formatterCache[prop];
            }
        }
    }
    for (var prop in contextCache) {
        if (contextCache.hasOwnProperty(prop)) {
            if (contextCache[prop] instanceof LocalizationContext && contextCache[prop].isRetained()) {
                contextCache[prop].release();
                // tslint:disable-next-line:no-dynamic-delete
                delete contextCache[prop];
            }
        }
    }
    FormatterFactory.instance().clearFormatterCache();
}
